import React, { useMemo, useState } from "react";
import { Loader2, Plus, Trash2, Trophy } from "lucide-react";
import Header from "components/Header";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  useCreateListedLeaderboardMutation,
  useGetListedLeaderboardsQuery,
  useGetProductsQuery,
  useSetListedLeaderboardStatusMutation,
  useSetListedLeaderboardVisibilityMutation,
  useUpdateListedLeaderboardMutation,
  useUploadListedLeaderboardImageMutation,
} from "state/api";
import { getImageUrl } from "utils/common";

const emptyTier = () => ({ winrange: "1", winpercent: "20" });

const emptyForm = () => ({
  title: "",
  description: "",
  startAt: "",
  endAt: "",
  visibleOnApp: true,
  adminStatus: "ACTIVE",
  wishaurContribution: "0",
  perEntryIncrease: "0.01",
  prizeTiers: [emptyTier(), { winrange: "2-10", winpercent: "80" }],
  requireProductQuiz: false,
  qualifyProductId: "",
  qualifyRequiredEntries: "1",
  qualifyStartAt: "",
  qualifyEndAt: "",
});

function toLocalInput(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toIso(local) {
  return new Date(local).toISOString();
}

function parseWinRange(winrange) {
  const trimmed = String(winrange ?? "")
    .trim()
    .replace(/^#+/, "");
  if (!trimmed) return { start: 0, end: 0 };
  if (trimmed.includes("-")) {
    const [left, right] = trimmed.split("-");
    return {
      start: parseInt(left, 10) || 0,
      end: parseInt(right, 10) || 0,
    };
  }
  const single = parseInt(trimmed, 10) || 0;
  return { start: single, end: single };
}

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function formatRupees(value) {
  return `₹${roundMoney(value).toFixed(2)}`;
}

function tierAmounts(winrange, winpercent, totalPool) {
  const percent = Number(winpercent) || 0;
  const { start, end } = parseWinRange(winrange);
  const rangeSize = start >= 1 && end >= start ? end - start + 1 : 0;
  const tierTotal = (Number(totalPool) || 0) * (percent / 100);
  const perRank = rangeSize > 0 ? tierTotal / rangeSize : 0;
  return {
    tierTotal: roundMoney(tierTotal),
    perRank: roundMoney(perRank),
    rangeSize,
  };
}

const ListedLeaderboards = () => {
  const { data, isLoading, isError } = useGetListedLeaderboardsQuery();
  const { data: productsResponse } = useGetProductsQuery();
  const products = Array.isArray(productsResponse?.data)
    ? productsResponse.data
    : Array.isArray(productsResponse?.products)
    ? productsResponse.products
    : Array.isArray(productsResponse)
    ? productsResponse
    : [];
  const [createBoard, { isLoading: creating }] =
    useCreateListedLeaderboardMutation();
  const [updateBoard, { isLoading: updating }] =
    useUpdateListedLeaderboardMutation();
  const [setVisibility, { isLoading: togglingVis }] =
    useSetListedLeaderboardVisibilityMutation();
  const [setStatus, { isLoading: togglingStatus }] =
    useSetListedLeaderboardStatusMutation();
  const [uploadImage, { isLoading: uploading }] =
    useUploadListedLeaderboardImageMutation();

  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [editingEntryCount, setEditingEntryCount] = useState(0);
  const [imageFile, setImageFile] = useState(null);
  const [message, setMessage] = useState({ type: "", text: "" });

  const boards = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const saving = creating || updating || uploading;
  const percentSum = form.prizeTiers.reduce(
    (sum, tier) => sum + (Number(tier.winpercent) || 0),
    0
  );
  const liveEntryCount = editingId
    ? boards.find((board) => board.id === editingId)?.totalEntries ??
      editingEntryCount
    : 0;
  const livePool = roundMoney(
    Math.max(0, Number(form.wishaurContribution) || 0) +
      liveEntryCount * Math.max(0, Number(form.perEntryIncrease) || 0)
  );

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setTier = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      prizeTiers: prev.prizeTiers.map((tier, i) =>
        i === index ? { ...tier, [field]: value } : tier
      ),
    }));
  };

  const startCreate = () => {
    setEditingId(null);
    setEditingEntryCount(0);
    setForm(emptyForm());
    setImageFile(null);
    setMessage({ type: "", text: "" });
  };

  const startEdit = (board) => {
    setEditingId(board.id);
    setEditingEntryCount(Number(board.totalEntries) || 0);
    setForm({
      title: board.title || "",
      description: board.description || "",
      startAt: toLocalInput(board.startAt),
      endAt: toLocalInput(board.endAt),
      visibleOnApp: board.visibleOnApp !== false,
      adminStatus: board.adminStatus === "INACTIVE" ? "INACTIVE" : "ACTIVE",
      wishaurContribution: String(board.wishaurContribution ?? 0),
      perEntryIncrease: String(board.perEntryIncrease ?? 0.01),
      prizeTiers:
        board.prizeTiers?.length > 0
          ? board.prizeTiers.map((tier) => ({
              winrange: tier.winrange,
              winpercent: String(tier.winpercent),
            }))
          : [emptyTier()],
      requireProductQuiz: Boolean(board.qualifyProductId),
      qualifyProductId: board.qualifyProductId || "",
      qualifyRequiredEntries: String(board.qualifyRequiredEntries || 1),
      qualifyStartAt: toLocalInput(board.qualifyStartAt),
      qualifyEndAt: toLocalInput(board.qualifyEndAt),
    });
    setImageFile(null);
    setMessage({ type: "", text: "" });
  };

  const handleSave = async () => {
    setMessage({ type: "", text: "" });
    if (!form.title.trim() || !form.startAt || !form.endAt) {
      setMessage({ type: "error", text: "Title, start, and end are required." });
      return;
    }
    if (Math.abs(percentSum - 100) > 0.05) {
      setMessage({
        type: "error",
        text: `Prize percentages must sum to 100 (now ${percentSum}).`,
      });
      return;
    }
    if (form.requireProductQuiz && !form.qualifyProductId) {
      setMessage({
        type: "error",
        text: "Pick the product quiz players must complete first.",
      });
      return;
    }
    if (
      form.requireProductQuiz &&
      (!Number(form.qualifyRequiredEntries) ||
        Number(form.qualifyRequiredEntries) < 1)
    ) {
      setMessage({
        type: "error",
        text: "Required quiz entries must be at least 1.",
      });
      return;
    }
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      startAt: toIso(form.startAt),
      endAt: toIso(form.endAt),
      visibleOnApp: form.visibleOnApp === true,
      adminStatus: form.adminStatus,
      wishaurContribution: Number(form.wishaurContribution) || 0,
      perEntryIncrease: Math.max(0.01, Number(form.perEntryIncrease) || 0.01),
      prizeTiers: form.prizeTiers.map((tier) => ({
        winrange: String(tier.winrange || "").trim(),
        winpercent: Number(tier.winpercent) || 0,
      })),
      qualifyProductId: form.requireProductQuiz ? form.qualifyProductId : null,
      qualifyRequiredEntries: form.requireProductQuiz
        ? Number(form.qualifyRequiredEntries) || 0
        : 0,
      qualifyStartAt:
        form.requireProductQuiz && form.qualifyStartAt
          ? toIso(form.qualifyStartAt)
          : null,
      qualifyEndAt:
        form.requireProductQuiz && form.qualifyEndAt
          ? toIso(form.qualifyEndAt)
          : null,
    };
    try {
      let id = editingId;
      if (editingId) {
        await updateBoard({ id: editingId, ...payload }).unwrap();
      } else {
        const created = await createBoard(payload).unwrap();
        id = created?.id;
      }
      if (id && imageFile) {
        await uploadImage({ id, file: imageFile }).unwrap();
      }
      setMessage({
        type: "success",
        text: editingId ? "Leaderboard updated." : "Leaderboard created.",
      });
      if (!editingId) startCreate();
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.data?.message || "Failed to save leaderboard.",
      });
    }
  };

  return (
    <div className="p-4 md:p-6">
      <Header
        title="Listed Leaderboards"
        subtitle="Create timed boards. Optionally require a product quiz (with date range and entry count) before Play here unlocks."
      />

      {message.text ? (
        <p
          className={`mb-4 text-sm ${
            message.type === "error" ? "text-red-600" : "text-emerald-700"
          }`}
        >
          {message.text}
        </p>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <section className="rounded-lg border bg-card p-4">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              {editingId ? "Edit leaderboard" : "Create leaderboard"}
            </h2>
            {editingId ? (
              <Button variant="outline" size="sm" onClick={startCreate}>
                New
              </Button>
            ) : null}
          </div>

          <div className="grid gap-3">
            <Input
              placeholder="Title"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
            />
            <textarea
              className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Description"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
            />
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm">
                Start
                <Input
                  type="datetime-local"
                  className="mt-1"
                  value={form.startAt}
                  onChange={(e) => setField("startAt", e.target.value)}
                />
              </label>
              <label className="text-sm">
                End
                <Input
                  type="datetime-local"
                  className="mt-1"
                  value={form.endAt}
                  onChange={(e) => setField("endAt", e.target.value)}
                />
              </label>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-sm">
                WishAur contribution (₹)
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1"
                  value={form.wishaurContribution}
                  onChange={(e) =>
                    setField("wishaurContribution", e.target.value)
                  }
                />
              </label>
              <label className="text-sm">
                Per-entry increase (₹, min 0.01)
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className="mt-1"
                  value={form.perEntryIncrease}
                  onChange={(e) => setField("perEntryIncrease", e.target.value)}
                />
              </label>
            </div>
            <label className="text-sm">
              Image
              <Input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="mt-1"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
            </label>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.visibleOnApp}
                  onChange={(e) => setField("visibleOnApp", e.target.checked)}
                />
                Visible on app
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.adminStatus === "ACTIVE"}
                  onChange={(e) =>
                    setField(
                      "adminStatus",
                      e.target.checked ? "ACTIVE" : "INACTIVE"
                    )
                  }
                />
                Active
              </label>
            </div>

            <div className="rounded-md border p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={form.requireProductQuiz}
                  onChange={(e) =>
                    setField("requireProductQuiz", e.target.checked)
                  }
                />
                Require a product quiz before Play here
              </label>
              <p className="mt-1 text-xs text-muted-foreground">
                Players must finish this many quiz entries on the selected
                product in the date range. After that they can Play here and
                count on this board.
              </p>
              {form.requireProductQuiz ? (
                <div className="mt-3 grid gap-3">
                  <label className="text-sm">
                    Product
                    <select
                      className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      value={form.qualifyProductId}
                      onChange={(e) =>
                        setField("qualifyProductId", e.target.value)
                      }
                    >
                      <option value="">Select product</option>
                      {products.map((product) => {
                        const id = product._id || product.id;
                        return (
                          <option key={id} value={id}>
                            {product.productName || id}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                  <label className="text-sm">
                    Required quiz entries
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      className="mt-1"
                      value={form.qualifyRequiredEntries}
                      onChange={(e) =>
                        setField("qualifyRequiredEntries", e.target.value)
                      }
                    />
                  </label>
                  <div className="grid gap-3 md:grid-cols-2">
                    <label className="text-sm">
                      Qualify from
                      <Input
                        type="datetime-local"
                        className="mt-1"
                        value={form.qualifyStartAt}
                        onChange={(e) =>
                          setField("qualifyStartAt", e.target.value)
                        }
                      />
                    </label>
                    <label className="text-sm">
                      Qualify until
                      <Input
                        type="datetime-local"
                        className="mt-1"
                        value={form.qualifyEndAt}
                        onChange={(e) =>
                          setField("qualifyEndAt", e.target.value)
                        }
                      />
                    </label>
                  </div>
                </div>
              ) : null}
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-medium">
                  Prize tiers · {percentSum}% of {formatRupees(livePool)} pot
                  {liveEntryCount > 0
                    ? ` · ${liveEntryCount} entries`
                    : " · 0 entries"}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      prizeTiers: [...prev.prizeTiers, emptyTier()],
                    }))
                  }
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add rank
                </Button>
              </div>
              <div className="grid gap-2">
                {form.prizeTiers.map((tier, index) => {
                  const amounts = tierAmounts(
                    tier.winrange,
                    tier.winpercent,
                    livePool
                  );
                  return (
                    <div
                      key={`${tier.winrange}-${index}`}
                      className="flex items-center gap-2"
                    >
                      <Input
                        placeholder="1 or 2-10"
                        value={tier.winrange}
                        onChange={(e) =>
                          setTier(index, "winrange", e.target.value)
                        }
                      />
                      <Input
                        type="number"
                        min="0.01"
                        step="0.01"
                        placeholder="%"
                        value={tier.winpercent}
                        onChange={(e) =>
                          setTier(index, "winpercent", e.target.value)
                        }
                      />
                      <div className="min-w-[118px] text-right text-xs leading-tight">
                        <p className="font-semibold">
                          {amounts.rangeSize > 1
                            ? `${formatRupees(amounts.perRank)} each`
                            : formatRupees(amounts.perRank)}
                        </p>
                        <p className="text-muted-foreground">
                          {formatRupees(amounts.tierTotal)} of pot
                        </p>
                      </div>
                      {form.prizeTiers.length > 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              prizeTiers: prev.prizeTiers.filter(
                                (_, i) => i !== index
                              ),
                            }))
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {editingId ? "Save changes" : "Create leaderboard"}
            </Button>
          </div>
        </section>

        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-4 text-lg font-semibold">All listings</h2>
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : isError ? (
            <p className="text-sm text-red-600">Failed to load leaderboards.</p>
          ) : boards.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No listed leaderboards yet.
            </p>
          ) : (
            <div className="grid gap-3">
              {boards.map((board) => (
                <article
                  key={board.id}
                  className="rounded-md border p-3 text-sm"
                >
                  <div className="flex gap-3">
                    {board.imageKey ? (
                      <img
                        src={getImageUrl(board.imageKey)}
                        alt=""
                        className="h-14 w-14 rounded object-cover"
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded bg-muted">
                        <Trophy className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">{board.title}</p>
                      <p className="text-muted-foreground">
                        {board.runtimeStatus} · {board.totalEntries} entries · ₹
                        {Number(board.totalPool || 0).toFixed(2)} · payout{" "}
                        {board.payoutStatus}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(board.startAt).toLocaleString()} →{" "}
                        {new Date(board.endAt).toLocaleString()}
                      </p>
                      {board.qualifyProductId ? (
                        <p className="mt-1 text-xs text-amber-700">
                          Unlock: play {board.qualifyProductName || "product"}{" "}
                          {board.qualifyRequiredEntries} time
                          {board.qualifyRequiredEntries === 1 ? "" : "s"}
                          {board.qualifyStartAt || board.qualifyEndAt
                            ? ` · ${
                                board.qualifyStartAt
                                  ? new Date(
                                      board.qualifyStartAt
                                    ).toLocaleString()
                                  : "any time"
                              } → ${
                                board.qualifyEndAt
                                  ? new Date(
                                      board.qualifyEndAt
                                    ).toLocaleString()
                                  : "no end"
                              }`
                            : ""}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => startEdit(board)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={togglingVis}
                      onClick={() =>
                        setVisibility({
                          id: board.id,
                          visibleOnApp: !board.visibleOnApp,
                        })
                      }
                    >
                      {board.visibleOnApp ? "Hide from app" : "Show on app"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={togglingStatus}
                      onClick={() =>
                        setStatus({
                          id: board.id,
                          adminStatus:
                            board.adminStatus === "ACTIVE"
                              ? "INACTIVE"
                              : "ACTIVE",
                        })
                      }
                    >
                      {board.adminStatus === "ACTIVE" ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ListedLeaderboards;
